import { useMemo } from 'react';

import Icon from './Icon.jsx';
import PathGrid from './PathGrid.jsx';
import { XP_REWARDS } from '../lib/xp.js';
import { practiceStats } from '../lib/practice.js';
import { QUESTIONS } from '../data/questions.js';
import { VERSES } from '../data/verses.js';
import { dailyNumber } from '../lib/daily.js';

/**
 * The practice hub.
 *
 * This replaced a single Quiz screen. One fixed quiz is a thin thing to come
 * back to daily, and it left nowhere to put a second kind of drill — so the
 * section is now a choice of modes, and adding another means adding one entry
 * to the list below.
 *
 * Every card shows a real count drawn from what the player has actually
 * answered, so a mode never offers a session it cannot fill, and Weak Spots
 * disables itself rather than opening empty.
 */
export default function PracticeScreen({
  answers,
  learning,
  daily,
  games,
  isPremium,
  onStart,
  onUpgrade,
}) {
  const stats = useMemo(() => practiceStats(answers), [answers]);
  const reviewCount = useMemo(
    () => VERSES.filter((v) => learning?.[v.id] === 'learning').length,
    [learning],
  );

  // The Daily is a mode like the others rather than a tab of its own — it
  // belongs with the practice, and a fifth destination in the nav would cost
  // more attention than it is worth. It leads the list because it expires.
  const today = dailyNumber();
  const playedToday = daily?.day === today;

  const modes = [
    {
      id: 'daily',
      accent: 'gold',
      note: `+${XP_REWARDS.quizCorrect} XP a correct answer`,
      reward: true,
      title: 'The Daily',
      blurb: 'Five questions. Everyone gets the same five.',
      glyph: 'radiance',
      tone: 'gold',
      count: playedToday
        ? `done — ${daily.results.filter(Boolean).length}/${daily.results.length}${
            daily.streak > 1 ? ` · ${daily.streak} days running` : ''
          }`
        : `#${today} · today’s five`,
      ready: true,
      featured: !playedToday,
    },
    {
      id: 'beforeafter',
      accent: 'indigo',
      title: 'Before or After',
      blurb: 'Two events. Which came first? Keep going until you slip.',
      glyph: 'clock',
      tone: 'amethyst',
      count: games?.beforeAfterBest
        ? `best run: ${games.beforeAfterBest} in a row`
        : 'a chain, not a quiz',
      ready: true,
    },
    {
      id: 'thirdday',
      accent: 'emerald',
      title: 'The Third Day',
      blurb: 'Every right answer walks you a step nearer the garden',
      glyph: 'arch',
      tone: 'sage',
      count: games?.thirdDayBest
        ? `best walk: ${games.thirdDayBest}`
        : 'eight steps, three chances',
      ready: true,
    },
    {
      id: 'twentyfaces',
      accent: 'violet',
      title: 'Twenty Faces',
      blurb: 'Twenty portraits, one to find. Ask, and the rest turn over.',
      glyph: 'arch',
      tone: 'amethyst',
      count: games?.twentyFacesBest
        ? `best: ${games.twentyFacesBest}`
        : 'guess who, with the apostles',
      ready: true,
    },
    {
      id: 'toscale',
      accent: 'amber',
      title: 'To Scale',
      blurb: 'Scripture gives the measurements. Can you picture them?',
      glyph: 'arch',
      tone: 'gold',
      count: games?.toScaleBest
        ? `best: ${games.toScaleBest}/500`
        : 'five things, guess the size',
      ready: true,
    },
    {
      id: 'quiz',
      accent: 'crimson',
      note: `+${XP_REWARDS.quizCorrect} XP a correct answer`,
      reward: true,
      title: 'Quick quiz',
      blurb: 'Ten questions, newest material first',
      glyph: 'flame',
      tone: 'ember',
      count: stats.remaining
        ? `${stats.remaining} still to learn`
        : `all ${stats.total} mastered — revising`,
      ready: true,
    },
    {
      id: 'timed',
      accent: 'amber',
      note: `+${XP_REWARDS.quizCorrect} XP, plus speed bonus`,
      reward: true,
      title: 'Beat the clock',
      blurb: 'Fifteen seconds a question, bonus XP for speed',
      glyph: 'clock',
      tone: 'amethyst',
      count: `${Math.min(10, stats.total)} questions · 15s each`,
      ready: true,
    },
    {
      id: 'weak',
      accent: 'rose',
      title: 'Weak spots',
      blurb: 'Only the questions you have got wrong',
      glyph: 'target',
      tone: 'rose',
      count: stats.weak ? `${stats.weak} to put right` : 'nothing missed yet',
      ready: stats.weak > 0,
    },
    {
      id: 'recall',
      accent: 'blue',
      note: `+${XP_REWARDS.quizCorrect} XP a verse`,
      reward: true,
      title: 'Verse recall',
      blurb: 'Fill in the missing word from memory',
      glyph: 'book',
      tone: 'gold',
      count: `${VERSES.length} verses in the deck`,
      ready: true,
    },
    {
      id: 'review',
      accent: 'emerald',
      title: 'Review',
      blurb: 'Verses you marked “still learning”',
      glyph: 'renew',
      tone: 'sage',
      count: reviewCount ? `${reviewCount} waiting` : 'nothing to review',
      ready: reviewCount > 0,
    },
    {
      id: 'character',
      title: 'Who Are You?',
      blurb: 'Find the Bible character you are most like',
      glyph: 'people',
      reward: true,
      accent: 'violet',
      note: `+${XP_REWARDS.characterQuiz} XP once`,
      count: 'ten questions',
      ready: true,
    },
    {
      id: 'paths',
      accent: 'teal',
      title: 'Book by book',
      blurb: 'Study Genesis, Psalms, John end to end',
      glyph: 'arch',
      tone: 'slate',
      count: 'choose a book',
      ready: true,
    },
  ];

  return (
    <section className="screen">
      <div className="screen__heading">
        <h2>Practice</h2>
        <p>What do you want to train today?</p>
      </div>

      <div className="mastery">
        <div className="mastery__bar">
          <span style={{ width: `${Math.round(stats.progress * 100)}%` }} />
        </div>
        <span className="mastery__label">
          {stats.mastered} of {stats.total} questions mastered
        </span>
      </div>

      <PathGrid
        heading="Choose Your Path"
        cards={modes.map((mode) => ({
          id: mode.id,
          icon: mode.glyph,
          accent: mode.accent,
          title: mode.title,
          blurb: mode.blurb,
          // A fixed reward reads as a reward; live state stays quiet, so
          // "3 waiting" is never mistaken for a prize.
          note: mode.ready === false ? mode.count : (mode.note ?? mode.count),
          tone: mode.ready !== false && mode.reward ? 'reward' : undefined,
          ready: mode.ready,
          onSelect: () => onStart(mode.id),
        }))}
      />

      {!isPremium ? (
        <button type="button" className="upsell" onClick={onUpgrade}>
          <span className="upsell__glyph">
            <Icon name="radiance" size={20} />
          </span>
          <span>
            <strong>Practise without limits</strong>
            <span>
              The free tier caps each session at five questions. Premium opens all{' '}
              {QUESTIONS.length}.
            </span>
          </span>
          <span className="upsell__chev">
            <Icon name="arrow" size={18} />
          </span>
        </button>
      ) : null}
    </section>
  );
}
